/**
 * Where a search result goes, and what a command palette can do.
 *
 * The API returns typed rows and no URLs on purpose — it does not own the
 * front end's routing, and renaming a route here should not need an API
 * deploy. That mapping lives in this file, next to the routes it mirrors.
 */
import { UserRole } from '@manpower/shared';
import type { IconName } from '@/components/primitives';

export type SearchResultType =
  | 'COMPANY'
  | 'JOB'
  | 'CANDIDATE'
  | 'APPLICATION'
  | 'INTERVIEW'
  | 'DEPLOYMENT'
  | 'INVOICE';

export interface SearchHit {
  id: string;
  title: string;
  subtitle: string | null;
  code: string | null;
}

export interface SearchGroup {
  type: SearchResultType;
  label: string;
  hits: SearchHit[];
}

const STAFF_ROLES: UserRole[] = [UserRole.STAFF, UserRole.ADMIN, UserRole.SUPER_ADMIN];

/**
 * The same result type means different screens to different people: a JOB is
 * a public ad to a candidate, an editable vacancy to the employer who posted
 * it, and a moderation row to staff. One shared table would send an employer
 * to the public page for their own draft.
 */
export function resultHref(type: SearchResultType, id: string, role: UserRole): string {
  const isStaff = STAFF_ROLES.includes(role);

  switch (type) {
    case 'JOB':
      if (role === UserRole.EMPLOYER) return `/employer/jobs/${id}`;
      if (isStaff) return `/admin/jobs?q=${encodeURIComponent(id)}`;
      // Candidates get the public ad, addressed by slug the way a shared link is.
      return `/jobs/${id}`;
    case 'COMPANY':
      return isStaff ? `/admin/employers/${id}` : `/companies/${id}`;
    case 'CANDIDATE':
      return `/admin/seekers?q=${encodeURIComponent(id)}`;
    case 'APPLICATION':
      return role === UserRole.EMPLOYER ? `/employer/applicants?applicationId=${id}` : '/seeker/applications';
    case 'INTERVIEW':
      return '/employer/interviews';
    case 'DEPLOYMENT':
      return role === UserRole.EMPLOYER ? '/employer/deployments' : '/admin/deployments';
    case 'INVOICE':
      return role === UserRole.EMPLOYER ? `/employer/invoices/${id}` : `/admin/invoices/${id}`;
  }
}

export interface PaletteCommand {
  label: string;
  href: string;
  icon: IconName;
  roles: UserRole[];
}

/**
 * Navigation, not data.
 *
 * Each entry is only a shortcut to a route that already enforces its own role
 * and permission checks, so listing one to somebody who cannot use it costs a
 * redirect, not an authorisation hole. They are still filtered by role here,
 * because offering an admin queue to a candidate is a bad answer even when it
 * is a safe one.
 */
export const COMMANDS: PaletteCommand[] = [
  // Employer
  { label: 'Create vacancy', href: '/employer/jobs/new', icon: 'plus', roles: [UserRole.EMPLOYER] },
  { label: 'Review candidates', href: '/employer/applicants', icon: 'users', roles: [UserRole.EMPLOYER] },
  { label: 'Search candidate pool', href: '/employer/candidates', icon: 'search', roles: [UserRole.EMPLOYER] },
  { label: 'Today’s interviews', href: '/employer/interviews', icon: 'calendar', roles: [UserRole.EMPLOYER] },
  { label: 'Workforce', href: '/employer/deployments', icon: 'factory', roles: [UserRole.EMPLOYER] },
  { label: 'Invoices', href: '/employer/invoices', icon: 'invoice', roles: [UserRole.EMPLOYER] },
  { label: 'Company profile', href: '/employer/company', icon: 'building', roles: [UserRole.EMPLOYER] },

  // Staff and admin
  { label: 'Open verification queue', href: '/admin/verification', icon: 'clipboard', roles: STAFF_ROLES },
  { label: 'Moderate vacancies', href: '/admin/jobs?status=PENDING_APPROVAL', icon: 'briefcase', roles: STAFF_ROLES },
  { label: 'Find company', href: '/admin/employers', icon: 'building', roles: STAFF_ROLES },
  { label: 'Find candidate', href: '/admin/seekers', icon: 'users', roles: STAFF_ROLES },
  { label: 'Verify documents', href: '/admin/documents', icon: 'file', roles: STAFF_ROLES },
  { label: 'Trust and safety', href: '/admin/trust', icon: 'shield', roles: STAFF_ROLES },
  { label: 'Client indents', href: '/admin/requirements', icon: 'clipboard', roles: STAFF_ROLES },
  { label: 'Attendance', href: '/admin/attendance', icon: 'calendar', roles: STAFF_ROLES },
  { label: 'Payroll', href: '/admin/payroll', icon: 'wallet', roles: STAFF_ROLES },
  { label: 'Reports', href: '/admin/reports', icon: 'chart', roles: STAFF_ROLES },

  // Candidate
  { label: 'Find jobs', href: '/jobs', icon: 'search', roles: [UserRole.SEEKER] },
  { label: 'My applications', href: '/seeker/applications', icon: 'clipboard', roles: [UserRole.SEEKER] },
  { label: 'Saved jobs', href: '/seeker/saved', icon: 'bookmark', roles: [UserRole.SEEKER] },
  { label: 'My documents', href: '/seeker/documents', icon: 'file', roles: [UserRole.SEEKER] },
  { label: 'Complete my profile', href: '/seeker/profile', icon: 'user', roles: [UserRole.SEEKER] },
];
